/**
 * @fileoverview Grow's advice (the tips and notices) as the server placed it,
 * and the season review's own asks.
 *
 * The tips and notices are rules (the backend's Grow settings, the one rules
 * list: advice). The server decides which one shows at each place, for this
 * person, now (the to-do rebuild, batch 5; the owner's rule: the backend
 * decides, the app only displays): the brief's todo.reads, one per place, with
 * the person's name and the owner's already in its words. This provider never
 * asks for the whole brief itself (W24 Job D; owner's yes, 30 Oct): it takes
 * the advice from a brief the screen already holds (Today, Plants, Beds and
 * Weather read it for themselves), and where none is held or read (Settings,
 * What to plant, a plant page, and before the garden has a postcode) it asks
 * for the same advice on its own (GET /today/brief/reads, a small answer).
 * Which brief is held is read from the query cache: a Today brief for this
 * garden and language with an answer, or one a screen is reading (an observer
 * on it); the advice alone is asked for only once the screen has mounted and
 * none is, so Today's first load does not ask for both. This provider hands
 * each screen the advice
 * for its place and says back what happened to it: seen (once a day, when it
 * is drawn), put away, done (its button), or an event that finishes advice
 * waiting for it (a plant added, Photos opened, the app opened from the home
 * screen; only an event the brief's readEvents lists). The server answers
 * with the person's advice afresh, which is put where the advice came from at
 * once (on the brief held, or the advice alone).
 *
 * A piece of advice put away or done in this visit is not drawn again while
 * the answer is on its way. Nothing is kept in the browser.
 *
 * The season review's milestone asks (SeasonMoments) are not advice rules but
 * keep their marks with the tips' (markKey, POST /tips/:key/:mark). The tips'
 * list (GET /tips: every rule's title, body and audience, and the seen rows)
 * is not read (W24 Job C): no screen draws it. Whether the person is tending
 * someone else's garden is the garden place's word (useGardenPostcode `own`).
 *
 * Where: TipsProvider wraps the signed-in app in the Layout.
 * API: a Today brief a screen holds (todo.reads, todo.readEvents; read from the
 * query cache, never asked for here); GET /today/brief/reads?lang=&device=
 * where none is (getBriefReads); POST
 * /todo/marks for advice (api/todo markRead); POST /tips/:key/:mark (the
 * season asks); POST /tips/beta once for a ?beta=1 visit; GET /garden-place
 * (useGardenPostcode, shared with the header: whose garden this is).
 * Words: none of its own; the advice carries the server's words.
 * Watch out: a mark that fails on our side is reported (Sentry) and says
 * nothing: the advice simply shows once more next time.
 *
 * @module tips/TipsContext
 */

import React from 'react';
import { useQuery, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useInstallPrompt, deviceNow } from '@/features/welcome/useInstallPrompt';
import { isBetaArrival } from '@/features/welcome/arrival';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { getBriefReads, type BriefReads, type TodayBrief } from '@/shared/api/todayBrief';
import { markRead, type ReadMark } from '@/shared/api/todo';
import { reportServerFailure } from '@/shared/lib/serverFailure';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { briefKeyStart } from '@/shared/lib/briefFreshness';
import { joinBeta, markTip } from './api';
import { fill } from './rules';
import type { Advice, AdviceMark, AdvicePlace, TipEvent, TipMark, TipWords } from './types';
import { happenedPlan } from './happenedRules';
import { READS_QUERY_KEY, TipsContext, type TipsApi } from './useTips';

/**
 * One season ask's key in this visit: the ask and the year it counts for.
 *
 * @param key - The ask's key.
 * @param scope - The season's year.
 * @returns "key|scope".
 */
const idOf = (key: string, scope: number): string => `${key}|${scope}`;

/** The advice already said seen in this visit, by key (the server's seen is once a day; this keeps the app from asking twice). */
const seenSaid = new Set<string>();

/** A Today brief in the cache, as the tips read it: its key, its answer, whether it failed. */
interface HeldBrief {
  /** The key it is kept under: [the brief's key, garden, language, unit, days] (lib/briefFreshness briefKey). */
  key: readonly unknown[];
  /** Its answer, or undefined while a screen is still reading it. */
  brief: TodayBrief | undefined;
  /** The screen's read of it failed. */
  failed: boolean;
}

/**
 * The Today brief a screen holds for this garden and language: one with an answer, else one a screen is reading (an
 * observer on it). Null when no screen holds or reads one (Settings, What to plant, a plant page).
 *
 * @param client - The app's query client.
 * @param garden - The garden being tended.
 * @param language - The words' language.
 * @returns The brief's query, or null.
 */
function heldBriefQuery(client: QueryClient, garden: number | null, language: string): Query | null {
  const found = client.getQueryCache().findAll({ queryKey: briefKeyStart(garden, language) });
  return found.find((query) => query.state.data !== undefined && query.getObserversCount() > 0)
    ?? found.find((query) => query.state.data !== undefined)
    ?? found.find((query) => query.getObserversCount() > 0)
    ?? null;
}

/**
 * The Today brief a screen holds, kept in step with the query cache (useSyncExternalStore): the same object while
 * nothing about it changes, so the provider redraws only when the brief does.
 *
 * @param client - The app's query client.
 * @param garden - The garden being tended.
 * @param language - The words' language.
 * @returns The held brief, or null.
 */
function useHeldBrief(client: QueryClient, garden: number | null, language: string): HeldBrief | null {
  const last = React.useRef<HeldBrief | null>(null);
  const subscribe = React.useCallback((changed: () => void) => client.getQueryCache().subscribe(changed), [client]);
  const read = React.useCallback((): HeldBrief | null => {
    const query = heldBriefQuery(client, garden, language);
    const next = query ? { key: query.queryKey, brief: query.state.data as TodayBrief | undefined, failed: query.state.status === 'error' } : null;
    const was = last.current;
    const same = was !== null && next !== null && was.brief === next.brief && was.failed === next.failed
      && JSON.stringify(was.key) === JSON.stringify(next.key);
    if (was === next || same) return was;
    last.current = next;
    return next;
  }, [client, garden, language]);
  return React.useSyncExternalStore(subscribe, read, read);
}

/**
 * Gives every screen the advice (useTips): what the server placed where, its
 * words, and saying what happened to it. Mounted once, in the Layout.
 *
 * @param props.children - The signed-in app.
 * @returns The provider around the app.
 */
export function TipsProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { mysqlUser } = useAuth();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { installed } = useInstallPrompt();
  /** The garden place (shared with the header): whether the signed-in person owns the garden being looked at. */
  const place = useGardenPostcode();
  const signedIn = Boolean(mysqlUser);
  const language = i18n.language ?? 'en';
  const device = deviceNow();
  /** No postcode, or one Grow cannot plan from (the server's word): no brief is read, so the advice is asked for alone. */
  const noZip = place.known && !place.usable;
  /** The garden being tended: the one chosen on this device, else the person's own (the brief's key's garden). */
  const garden = chosenGarden() ?? mysqlUser?.user_id ?? null;
  /** The brief a screen holds or is reading, if any: the advice is taken from it, and nothing more is asked for. */
  const held = useHeldBrief(queryClient, garden, language);
  const brief = held?.brief;
  /** Mounted: the screen's own reads have started, so a brief it reads is in the cache by now. */
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => { setMounted(true); }, []);

  // A beta link is kept on the account; the advice is read again once it is, so the beta line shows this visit.
  const [arrivedBeta] = React.useState(isBetaArrival);
  React.useEffect(() => {
    if (!arrivedBeta || !signedIn) return;
    void joinBeta().then(() => {
      void queryClient.invalidateQueries({ queryKey: briefKeyStart() });
      void queryClient.invalidateQueries({ queryKey: READS_QUERY_KEY });
    });
    // Once per visit: the key's own words do not change what joining does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedBeta, signedIn, queryClient]);

  /*
   * Where no brief is held or read (Settings, What to plant, a plant page; before a postcode, the postcode step and a
   * beta tester's line): the advice on its own. Only once mounted, so a screen that reads the brief (Today) has started
   * it first and the advice is not asked for twice.
   */
  const asksAlone = noZip || (held === null && mounted && place.known);
  const readsKey = [...READS_QUERY_KEY, garden, language, device];
  const { data: alone, isError: aloneFailed } = useQuery({
    queryKey: readsKey,
    queryFn: () => getBriefReads(language, device),
    enabled: signedIn && asksAlone,
    staleTime: QUERY_STALE_TIME_MS,
  });
  /** The brief's key, when the advice comes from a brief held (never before a postcode): a mark's answer is put there. */
  const briefKey = !noZip && held !== null ? held.key : null;
  /** The advice for each place now: the brief's, else the advice on its own. */
  const placed = briefKey ? brief?.todo.reads : alone?.reads;
  const events = (briefKey ? brief?.todo.readEvents : alone?.readEvents) ?? [];
  /** In, or not coming: Today waits for this before it chooses the welcome's step. A garden place that failed is not coming. */
  const ready = place.isError || (briefKey ? Boolean(brief) || Boolean(held?.failed) : Boolean(alone) || aloneFailed);
  /** How the held brief was asked for (its language, unit and days, from its key), which a mark repeats; null for none. */
  const ask = briefKey ? { lang: String(briefKey[2]), unit: briefKey[3] as 'F' | 'C', days: (briefKey[4] as number | null) ?? null } : null;

  /** The season asks shown in this visit: said shown once a visit. */
  const shownNow = React.useRef(new Set<string>());

  /** The advice put away or done in this visit: not drawn again while the server's answer is on its way. */
  const [gone, setGone] = React.useState<Set<string>>(() => new Set());

  /**
   * Puts the person's advice afresh where it is drawn from: on the brief (its fingerprint the server's, so the brief
   * held takes it), else the advice on its own.
   *
   * @param answer - The mark's answer.
   */
  const putAnswer = React.useCallback((answer: BriefReads & { fingerprint?: string | null }): void => {
    if (!briefKey) {
      queryClient.setQueryData(readsKey, answer);
      return;
    }
    queryClient.setQueryData<TodayBrief>(briefKey, (held) => (held?.todo ? {
      ...held,
      ...(answer.fingerprint ? { fingerprint: answer.fingerprint } : {}),
      todo: { ...held.todo, reads: answer.reads, readEvents: answer.readEvents, ...(answer.fingerprint ? { fingerprint: answer.fingerprint } : {}) },
    } : held));
    // The keys are new arrays each render: compared by their text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, JSON.stringify(briefKey), JSON.stringify(readsKey)]);

  // How the advice was asked for, kept in a ref so the marks stay one function from render to render (ask is a new
  // object each time).
  // briefKey: the held brief's key, else the start every brief's key shares (its first part is what the refresh invalidates).
  const asked = React.useRef({ language, device, ask, putAnswer, briefKey: briefKey ?? briefKeyStart() });
  asked.current = { language, device, ask, putAnswer, briefKey: briefKey ?? briefKeyStart() };

  /**
   * Says a mark on advice to the server; its answer (the advice afresh) is put in place. A failure on our side is
   * reported and says nothing: the advice shows once more next time.
   *
   * @param readMark - The mark.
   * @returns Done once the answer is in place.
   */
  const send = React.useCallback(async (readMark: ReadMark): Promise<void> => {
    const now = asked.current;
    try {
      // Asked as the advice was: the held brief's language, unit and days, else the language and device alone.
      const answer = await markRead(readMark, now.ask ? { ...now.ask, device: now.device } : { lang: now.language, device: now.device });
      if (readMark.action !== 'seen') asked.current.putAnswer(answer);
    } catch (error) {
      reportServerFailure(error, 'Advice mark');
    }
  }, []);

  /** The advice the server placed here, unless it was put away or done in this visit. */
  const read = React.useCallback((place: AdvicePlace): Advice | null => {
    const found = placed?.[place] ?? null;
    return found && !gone.has(found.key) ? found : null;
  }, [placed, gone]);

  /** Says seen (once a day: not when the server already has it today, nor twice in a visit), put away or done. */
  const mark = React.useCallback((advice: Advice, what: AdviceMark): void => {
    if (what === 'seen') {
      if (advice.seenToday || seenSaid.has(advice.key)) return;
      seenSaid.add(advice.key);
    } else {
      setGone((prev) => new Set(prev).add(advice.key));
    }
    void send({ key: advice.key, action: what });
  }, [send]);

  /** Its words with the moment's own put in. */
  const words = React.useCallback((advice: Advice, extra: Record<string, string | null | undefined> = {}): TipWords => ({
    title: fill(advice.title, extra),
    body: fill(advice.body, extra),
    button: advice.button?.text ?? '',
  }), []);

  /** The events the advice waits for now, read by happened without making it a new function each render. */
  const waiting = React.useRef(events);
  waiting.current = events;
  /** The events already told in this visit: each finishes its advice once. */
  const told = React.useRef(new Set<TipEvent>());
  /** Something happened: told to the server only when advice is waiting for it, and once a visit. */
  const happened = React.useCallback((event: TipEvent, options?: { gardenWritten?: boolean }): void => {
    const plan = happenedPlan(event, waiting.current.includes(event), told.current.has(event), options?.gardenWritten === true);
    if (plan.tell) told.current.add(event);
    /** Reads Today's brief and the advice again, so what the event changed shows with no reload. */
    const refresh = (): void => {
      if (!plan.refresh) return;
      // Not the brief again when the write already invalidated it (gardenWritten): that cancelled the fetch under way (C81).
      if (plan.refreshBrief) void queryClient.invalidateQueries({ queryKey: [asked.current.briefKey[0]] });
      void queryClient.invalidateQueries({ queryKey: READS_QUERY_KEY });
    };
    if (plan.tell) void send({ action: 'done', event }).then(refresh);
    else refresh();
  }, [send, queryClient]);

  // Opened from the home screen: the advice waiting for that is finished (once the brief says some is).
  const installedWaited = installed && events.includes('installed');
  React.useEffect(() => { if (installedWaited) happened('installed'); }, [installedWaited, happened]);

  /**
   * Marks a season ask shown (once per visit), done or put away, on the account (POST /tips/:key/:mark).
   *
   * @param key - The ask's key.
   * @param scope - The season's year.
   * @param what - What happened.
   */
  const markKey = React.useCallback((key: string, scope: number, what: TipMark): void => {
    if (what === 'shown') {
      const id = idOf(key, scope);
      if (shownNow.current.has(id)) return;
      shownNow.current.add(id);
    }
    void markTip(key, what, scope);
  }, []);

  /**
   * Whether a season ask has been made. No screen asks (W24 Job C): the seen rows are not read, so it says what it
   * always said before they arrived, asked, and nothing pops up. It stays only because useTips' TipsApi still names it.
   *
   * @returns True.
   */
  const askedOnce = React.useCallback((): boolean => true, []);

  /** Looking at someone else's garden, as the garden place says (`own`); unknown until the account and the place are in. */
  const tending = mysqlUser && place.known ? !place.own : null;

  /** What useTips gives: one object, kept while nothing in it changes. */
  const api = React.useMemo<TipsApi>(
    () => ({ read, words, mark, happened, askedOnce, markKey, ready, tending }),
    [read, words, mark, happened, askedOnce, markKey, ready, tending],
  );
  return <TipsContext.Provider value={api}>{children}</TipsContext.Provider>;
}
